<template>
  <div class="note-card-stack">
    <section class="note-card-stack__stage">
      <div
        v-for="card in orderedCards"
        :key="card._id"
        class="note-card"
        :class="{ 'note-card--top': card._id === topId }"
        :style="cardStyle(card)"
        @touchstart="onDown($event, card._id)"
        @mousedown="onDown($event, card._id)"
      >
        <div class="note-card__head">
          <span
            class="note-card__badge"
            :style="{ background: card._palette.badgeBg }"
          >{{ cardLabel(card) }}</span>
        </div>
        <h3 class="note-card__theme">{{ card.theme || '未命名笔记' }}</h3>
        <div class="note-card__body">
          <!-- eslint-disable-next-line vue/no-v-html -->
          <div class="note-card__content" :data-id="card._id" v-html="card._html"></div>
          <div
            v-if="overflowMap[card._id]"
            class="note-card__fade"
            :style="{ backgroundImage: card._fade }"
          >
            <span class="note-card__ellipsis">...</span>
          </div>
        </div>
      </div>

      <div v-if="loading" class="note-card-stack__status">
        <i class="el-icon-loading"></i>
        <span>加载中...</span>
      </div>
      <div v-else-if="!cards.length" class="note-card-stack__status">暂无笔记</div>
    </section>

    <button
      type="button"
      class="note-card-stack__more"
      @click="goNoteList"
    >
      <span>查看更多</span>
      <span class="note-card-stack__more-arrow">&#8594;</span>
    </button>
  </div>
</template>

<script>
import { getApiErrorMessage } from '@/utils/apiErrorMessage'
import { fetchUserNoteList } from '@/api/userNote'
import { renderNoteContent } from '@/utils/renderNoteContent'

const MAX_CARDS = 5
// 自然堆叠：轻微上移 + 缩小 + 很小的角度错开，保证卡片之间不完全重叠
const STACK_OFFSET_Y = 14
const STACK_SCALE_STEP = 0.04
const STACK_ROTATIONS = [0, -3, 3, -2.5, 4]
// 卡片固定配色（数组末位为最前面的首张）：蓝、黄、灰、白、橙
// 灰/白黑字，其余白字；首张展示橙色
const CARD_PALETTE = [
  { bg: '#4253ff', text: '#ffffff', badgeBg: 'rgba(255, 255, 255, 0.22)' },
  { bg: '#f5b301', text: '#ffffff', badgeBg: 'rgba(255, 255, 255, 0.26)' },
  { bg: '#e7eaf1', text: '#1f2430', badgeBg: 'rgba(0, 0, 0, 0.10)' },
  { bg: '#ffffff', text: '#1f2430', badgeBg: 'rgba(0, 0, 0, 0.06)' },
  { bg: '#ff8a3d', text: '#ffffff', badgeBg: 'rgba(255, 255, 255, 0.24)' }
]
const SWIPE_THRESHOLD = 80
const TAP_THRESHOLD = 8
const FLY_DISTANCE = 700
const FLY_DURATION = 350
const MOUSE_IGNORE_AFTER_TOUCH_MS = 500
const EMPTY_DRAG = { id: null, startX: 0, startY: 0, dx: 0, dy: 0, active: false }

function buildFadeGradient(bg) {
  const color = bg || '#ffffff'
  const m = /^#([0-9a-f]{6})$/i.exec(color)
  if (!m) return `linear-gradient(to bottom, rgba(255, 255, 255, 0), ${color})`
  const r = parseInt(m[1].slice(0, 2), 16)
  const g = parseInt(m[1].slice(2, 4), 16)
  const b = parseInt(m[1].slice(4, 6), 16)
  return `linear-gradient(to bottom, rgba(${r}, ${g}, ${b}, 0) 0%, rgba(${r}, ${g}, ${b}, 0.9) 55%, rgba(${r}, ${g}, ${b}, 1) 100%)`
}

export default {
  name: 'NoteCardStack',
  data() {
    return {
      loading: false,
      cards: [],
      order: [],
      overflowMap: {},
      drag: { ...EMPTY_DRAG },
      flyingId: null,
      flyVec: { x: 0, y: 0 },
      noTransitionId: null
    }
  },
  computed: {
    topId() {
      return this.order.length ? this.order[this.order.length - 1] : null
    },
    cardMap() {
      const map = Object.create(null)
      this.cards.forEach((card) => {
        map[card._id] = card
      })
      return map
    },
    orderedCards() {
      return this.order.map((id) => this.cardMap[id]).filter(Boolean)
    }
  },
  created() {
    this._onMove = this.onMove.bind(this)
    this._onUp = this.onUp.bind(this)
    this._onResize = this.measureOverflow.bind(this)
    this._ignoreMouseUntil = 0
    this._listening = false
    this._flyTimer = null
  },
  mounted() {
    this.loadCards()
    window.addEventListener('resize', this._onResize)
    this.$bus.$on('refresh-note-cards', this.loadCards)
  },
  beforeDestroy() {
    this.detachWindowListeners()
    this.$bus.$off('refresh-note-cards', this.loadCards)
    window.removeEventListener('resize', this._onResize)
    this.clearFlyTimer()
  },
  methods: {
    clearFlyTimer() {
      if (this._flyTimer) {
        window.clearTimeout(this._flyTimer)
        this._flyTimer = null
      }
    },
    resetDrag() {
      this.drag = { ...EMPTY_DRAG }
    },
    async loadCards() {
      this.loading = true
      this.overflowMap = {}
      try {
        const data = await fetchUserNoteList({ pageNum: 1, pageSize: MAX_CARDS })
        const records = Array.isArray(data.records) ? data.records : []
        // 接口按 update_date 降序（最新在前）；堆叠约定数组末位为正面卡片，故反转
        const stacked = records.slice(0, MAX_CARDS).reverse()
        this.cards = stacked.map((item, index) => {
          const palette = CARD_PALETTE[index % CARD_PALETTE.length]
          return {
            _id: item.id != null ? String(item.id) : `note-${index}`,
            _angle: STACK_ROTATIONS[index % STACK_ROTATIONS.length],
            _palette: palette,
            _fade: buildFadeGradient(palette.bg),
            _html: renderNoteContent(item.noteContent) || '暂无笔记内容',
            summaryId: item.summaryId || item.idEzTextSummary,
            theme: item.theme,
            parentLabel: item.parentLabel,
            updateDate: item.updateDate || item.createDate || ''
          }
        })
        this.order = this.cards.map((c) => c._id)
      } catch (error) {
        console.error('加载笔记卡片失败:', error)
        this.$message.error(getApiErrorMessage(error, '加载笔记卡片失败'))
        this.cards = []
        this.order = []
      } finally {
        this.loading = false
        this.$nextTick(() => {
          window.requestAnimationFrame(() => this.measureOverflow())
        })
      }
    },
    measureOverflow() {
      if (!this.$el || !this.$el.querySelectorAll) return
      const map = {}
      this.$el.querySelectorAll('.note-card__content').forEach((el) => {
        const id = el.getAttribute('data-id')
        if (id != null) {
          map[id] = el.scrollHeight - el.clientHeight > 2
        }
      })
      this.overflowMap = map
    },
    goNoteList() {
      this.$router.push('/summary-list/notes')
    },
    cardLabel(card) {
      const label = card && card.parentLabel != null ? String(card.parentLabel).trim() : ''
      return label || '通用'
    },
    cardStyle(card) {
      const id = card._id
      const n = this.order.length
      const depth = this.order.indexOf(id)
      const palette = card._palette || {}

      if (id === this.flyingId) {
        return {
          zIndex: n + 1,
          opacity: 0,
          backgroundColor: palette.bg,
          color: palette.text,
          transition: `transform ${FLY_DURATION}ms ease, opacity ${FLY_DURATION}ms ease`,
          transform:
            `translate(-50%, -50%) translate(${this.flyVec.x}px, ${this.flyVec.y}px) rotate(${card._angle || 0}deg)`
        }
      }

      // back：距离最顶层（当前卡片）的层数，0 为正面卡片
      const back = n - 1 - depth
      // 角度固定绑定到卡片本身，切换层级后不变
      const angle = card._angle || 0
      const scale = 1 - back * STACK_SCALE_STEP
      let tx = 0
      let ty = -back * STACK_OFFSET_Y
      let extraRot = 0
      let transition = `transform ${FLY_DURATION}ms ease, opacity ${FLY_DURATION}ms ease`

      const isDragging = id === this.drag.id && this.drag.active
      if (isDragging) {
        tx = this.drag.dx
        ty = this.drag.dy
        extraRot = this.drag.dx * 0.03
        transition = 'none'
      }
      if (id === this.noTransitionId) {
        transition = 'none'
      }

      return {
        zIndex: depth,
        opacity: 1,
        backgroundColor: palette.bg,
        color: palette.text,
        transition,
        transform:
          `translate(-50%, -50%) translate(${tx}px, ${ty}px) rotate(${angle + extraRot}deg) scale(${scale})`
      }
    },
    getPoint(e) {
      if (e.touches && e.touches.length) {
        return { x: e.touches[0].clientX, y: e.touches[0].clientY }
      }
      if (e.changedTouches && e.changedTouches.length) {
        return { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY }
      }
      return { x: e.clientX, y: e.clientY }
    },
    onDown(e, id) {
      if (this.flyingId) return
      if (id !== this.topId) return
      // 触摸后浏览器可能合成 mousedown，短时忽略避免重复触发
      if (e.type === 'mousedown' && Date.now() < this._ignoreMouseUntil) return
      if (e.type === 'touchstart') {
        this._ignoreMouseUntil = Date.now() + MOUSE_IGNORE_AFTER_TOUCH_MS
      }
      const p = this.getPoint(e)
      this.drag = { id, startX: p.x, startY: p.y, dx: 0, dy: 0, active: true }
      this.attachWindowListeners()
    },
    onMove(e) {
      if (!this.drag.active) return
      if (e.cancelable && e.type === 'touchmove') e.preventDefault()
      const p = this.getPoint(e)
      this.drag.dx = p.x - this.drag.startX
      this.drag.dy = p.y - this.drag.startY
    },
    onUp() {
      if (!this.drag.active) return
      const { id, dx, dy } = this.drag
      const dist = Math.hypot(dx, dy)
      this.detachWindowListeners()
      if (dist >= SWIPE_THRESHOLD && this.order.length > 1) {
        this.commitSwipe(dx, dy)
      } else if (dist < TAP_THRESHOLD) {
        this.resetDrag()
        this.openCard(id)
      } else {
        this.resetDrag()
      }
    },
    openCard(id) {
      const card = this.cardMap[id]
      const summaryId = card && card.summaryId
      if (!summaryId) {
        this.$message.warning('关联总结不存在')
        return
      }
      this.$router.push({
        path: '/summary-list/detail',
        query: { from: 'note-cards', id: String(summaryId) }
      })
    },
    commitSwipe(dx, dy) {
      const id = this.topId
      if (id == null) return
      const dist = Math.hypot(dx, dy) || 1
      this.clearFlyTimer()
      this.flyingId = id
      this.flyVec = { x: (dx / dist) * FLY_DISTANCE, y: (dy / dist) * FLY_DISTANCE }
      this.resetDrag()

      this._flyTimer = window.setTimeout(() => {
        this._flyTimer = null
        const idx = this.order.indexOf(id)
        if (idx !== -1) {
          this.order.splice(idx, 1)
          this.order.unshift(id)
        }
        this.noTransitionId = id
        this.flyingId = null
        this.$nextTick(() => {
          window.requestAnimationFrame(() => {
            this.noTransitionId = null
          })
        })
      }, FLY_DURATION)
    },
    attachWindowListeners() {
      if (this._listening) return
      window.addEventListener('touchmove', this._onMove, { passive: false })
      window.addEventListener('touchend', this._onUp)
      window.addEventListener('touchcancel', this._onUp)
      window.addEventListener('mousemove', this._onMove)
      window.addEventListener('mouseup', this._onUp)
      this._listening = true
    },
    detachWindowListeners() {
      if (!this._listening) return
      window.removeEventListener('touchmove', this._onMove)
      window.removeEventListener('touchend', this._onUp)
      window.removeEventListener('touchcancel', this._onUp)
      window.removeEventListener('mousemove', this._onMove)
      window.removeEventListener('mouseup', this._onUp)
      this._listening = false
    }
  }
}
</script>

<style scoped>
.note-card-stack {
  position: relative;
  min-height: 100%;
  background: linear-gradient(180deg, #e6ecff 0%, #eef2fc 48%, #f6f8ff 100%);
  padding: calc(20px + env(safe-area-inset-top, 0px)) 0
    calc(var(--bottom-tab-height, 72px) + env(safe-area-inset-bottom, 0px));
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: center;
  -webkit-tap-highlight-color: transparent;
  overflow: hidden;
}

/* 卡片背后的主光晕（品牌蓝），聚焦卡片 */
.note-card-stack::before {
  content: '';
  position: absolute;
  top: 44%;
  left: 50%;
  width: 130vw;
  max-width: 680px;
  aspect-ratio: 1 / 1;
  transform: translate(-50%, -50%);
  background: radial-gradient(
    circle at center,
    rgba(66, 83, 255, 0.28) 0%,
    rgba(66, 83, 255, 0.12) 38%,
    rgba(66, 83, 255, 0) 70%
  );
  pointer-events: none;
  z-index: 0;
}

/* 辅助暖色光晕，增加层次氛围 */
.note-card-stack::after {
  content: '';
  position: absolute;
  top: 22%;
  right: -10%;
  width: 80vw;
  max-width: 380px;
  aspect-ratio: 1 / 1;
  background: radial-gradient(
    circle at center,
    rgba(255, 138, 61, 0.2) 0%,
    rgba(255, 138, 61, 0) 68%
  );
  pointer-events: none;
  z-index: 0;
}

.note-card-stack__stage {
  position: relative;
  z-index: 1;
  width: 100%;
  flex: none;
  height: 52vh;
  min-height: 380px;
  display: flex;
  align-items: center;
  justify-content: center;
  perspective: 1200px;
}

.note-card {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 76vw;
  max-width: 320px;
  height: 46vh;
  max-height: 400px;
  box-sizing: border-box;
  padding: 22px 20px;
  border-radius: 20px;
  background: #ffffff;
  border: 1px solid #eef2ff;
  box-shadow: 0 12px 30px rgba(31, 45, 120, 0.14);
  transform-origin: center center;
  will-change: transform;
  display: flex;
  flex-direction: column;
  user-select: none;
}

.note-card--top {
  cursor: grab;
}

.note-card--top:active {
  cursor: grabbing;
}

.note-card__head {
  display: flex;
  align-items: center;
  margin-bottom: 14px;
}

.note-card__badge {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 10px;
  border-radius: 9px;
  color: inherit;
  font-size: 12px;
  font-weight: 700;
}

.note-card__theme {
  margin: 0 0 12px;
  font-size: 18px;
  font-weight: 700;
  color: inherit;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
}

.note-card__body {
  position: relative;
  flex: 1;
  min-height: 0;
}

.note-card__content {
  height: 100%;
  font-size: 15px;
  color: inherit;
  opacity: 0.9;
  line-height: 1.7;
  overflow: hidden;
  word-break: break-word;
}

.note-card__fade {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 48px;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  pointer-events: none;
}

.note-card__ellipsis {
  font-size: 15px;
  font-weight: 700;
  line-height: 1.2;
  color: inherit;
  opacity: 0.9;
}

.note-card__content >>> .note-plain-text {
  white-space: pre-wrap;
}

.note-card__content >>> p {
  margin: 0 0 8px;
}

.note-card__content >>> p:last-child {
  margin-bottom: 0;
}

.note-card__content >>> h1,
.note-card__content >>> h2,
.note-card__content >>> h3,
.note-card__content >>> h4,
.note-card__content >>> h5,
.note-card__content >>> h6 {
  margin: 0 0 6px;
  font-weight: 700;
  line-height: 1.35;
}

.note-card__content >>> h1 { font-size: 1.2em; }
.note-card__content >>> h2 { font-size: 1.12em; }
.note-card__content >>> h3 { font-size: 1.06em; }

.note-card__content >>> ul,
.note-card__content >>> ol {
  margin: 0 0 8px;
  padding-left: 1.3em;
}

.note-card__content >>> li {
  margin-bottom: 3px;
}

.note-card__content >>> blockquote {
  margin: 0 0 8px;
  padding: 4px 10px;
  border-left: 3px solid currentColor;
  opacity: 0.85;
}

.note-card__content >>> code {
  padding: 1px 5px;
  border-radius: 4px;
  background: rgba(127, 127, 127, 0.22);
  font-family: Consolas, Monaco, 'Courier New', monospace;
  font-size: 0.9em;
}

.note-card__content >>> pre {
  margin: 0 0 8px;
  padding: 8px 10px;
  border-radius: 8px;
  background: rgba(127, 127, 127, 0.22);
  overflow-x: auto;
}

.note-card__content >>> pre code {
  padding: 0;
  background: transparent;
}

.note-card__content >>> a {
  color: inherit;
  text-decoration: underline;
}

.note-card__content >>> hr {
  margin: 8px 0;
  border: none;
  border-top: 1px solid currentColor;
  opacity: 0.4;
}

.note-card__content >>> img {
  max-width: 100%;
}

.note-card__content >>> table {
  width: 100%;
  margin: 0 0 8px;
  border-collapse: collapse;
  font-size: 0.92em;
}

.note-card__content >>> th,
.note-card__content >>> td {
  border: 1px solid currentColor;
  padding: 4px 6px;
  text-align: left;
}

.note-card-stack__more {
  position: relative;
  z-index: 1;
  display: flex;
  width: fit-content;
  align-items: center;
  gap: 8px;
  margin: 19px auto 0;
  padding: 6px 8px;
  border: none;
  background: transparent;
  color: #4253ff;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  transition: opacity 0.2s, transform 0.2s;
  animation: note-card-more-float 1.8s ease-in-out infinite;
}

.note-card-stack__more-arrow {
  font-size: 20px;
  line-height: 1;
  transition: transform 0.2s;
}

.note-card-stack__more:hover {
  opacity: 0.8;
}

.note-card-stack__more:hover .note-card-stack__more-arrow {
  transform: translateX(3px);
}

.note-card-stack__more:active {
  transform: scale(0.96);
  animation: none;
}

@keyframes note-card-more-float {
  0%,
  100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-4px);
  }
}

.note-card-stack__status {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #8a9bb8;
  font-size: 14px;
}

</style>
